import { defineStore, acceptHMRUpdate } from 'pinia'
import { mutate, query, entityToMutationInput,sanitizeGraphqlObject} from '@/services/graphql/client'
import { entityRegistry } from '@/config/entityRegistry'
import {fetchAllSummaries as fetchAllSummariesService }  from '@/services/entityService'
import {useLogger} from "@/composables/useLogger"
const {sendLog} = useLogger() 

function normalizeGraphqlError(err) {
  if (err.networkError) {
    return {
      type: "NETWORK",
      message: "Network error. Please check your connection."
    }
  }

  if (err.graphQLErrors?.length) {
    return {
      type: "VALIDATION",
      message: "Some fields are invalid. Please check your input."
    }
  }

  return {
    type: "UNKNOWN",
    message: "Something went wrong. Please try again."
  }
}

export const useEntityStore = defineStore('entity', {
  state: () => ({
    entities: Object.fromEntries(
      Object.keys(entityRegistry).map(type => [
        type,
        {
          items: [],
          selected: null,
          loading: false
        }
      ])
    ),
    loaded: false
  }),

  getters: {
    getEntityState: (state) => (type) => {
      return state.entities[type]
    },
    getEntityConfig:(state) => (entityId) => {
      return state.entities["entityConfigs"].items.find( r => r.entityId === entityId)
    },
    hasUserUndertheEntity: (state) => (entityType, entityId) => {
      return state.entities?.users?.items.find(user => user[entityType]?._id === entityId)
    },
    hasDepartmentUndertheEntity: (state) => (entityType, entityId) => {
      return state.entities?.departments?.items.find(user => user[entityType]?._id === entityId)
    }
  },

  actions: {
    initializeBuckets() {
      Object.keys(entityRegistry).forEach(type => {
        if (!this.entities[type]) {
          this.entities[type] = {
            items: [],
            formState: null,
            editorMode: null,
            detailLoaded: false,
            loading: false
          }
        }
      })
    },

    async fetchAllSummaries() {
      if (this.loaded) return

      this.initializeBuckets()

    const data = await fetchAllSummariesService()

    Object.keys(entityRegistry).forEach(type => {
      if(type === "entityConfigs") return
      this.entities[type].items = data[type] || []
    })
      this.loaded = true
    },

    async fetchDetail(type, id) {
      const config = entityRegistry[type]
      const bucket = this.entities[type]

      bucket.loading = true
      
      const { data } = await query(
        config.detailQuery,
        { id }
      )

      bucket.formState = structuredClone(data.item)
      bucket.editorMode = 'edit'
      bucket.detailLoaded = true
      bucket.loading = false
    },

    async fetchAllEntityConfig(){
      const config = entityRegistry["entityConfigs"]
      
    
      const { data } = await query(
        config.summaryField,
      )
      this.entities["entityConfigs"].items = data.items
    },    
    startCreate(type) {
      const bucket = this.entities[type]
      bucket.formState = {}
      bucket.editorMode = 'create'
      bucket.detailLoaded = true
    },

    async save(type) {
      const bucket = this.entities[type]
      const config = entityRegistry[type]

      const clone = structuredClone(bucket.formState)
      const sanitized = sanitizeGraphqlObject(clone)
      const localized = entityToMutationInput(sanitized, bucket.editorMode === 'create', type)
      const mutation =
        bucket.editorMode === 'create'
          ? config.createMutation
          : config.updateMutation
          
      try {
        const { data } = await mutate(
        mutation,
       { filter: {_id: bucket.formState._id}, record: localized }
      )
      const savedItem = data.saveItem.record
      if (bucket.editorMode === 'create') {
        const items = bucket.items || []
        bucket.items = [{...savedItem, id: savedItem._id}, ...items]
      } else {
        bucket.items = bucket.items.map(item =>
          item.id === savedItem._id ? {...savedItem, id: savedItem._id} : item
        )  
      }
      
      return {
        ok: true,
        data: data.updateEntity
      }

      } catch(err) {
        sendLog("error", "Fail_to_update_entity_store", err)
        return {
          ok: false,
          error: {
            type: "NETWORK",
            message: "Network error. Please check your connection."
          }
        }
      }
    },
    async delete(type, id) {
      const config = entityRegistry[type]
      const bucket = this.entities[type]

      await mutate(
        config.deleteMutation,
      { id })

      bucket.items = bucket.items.filter(i => i.id !== id)
    },

    resetEditor(type) {
      const bucket = this.entities[type]
      bucket.formState = null
      bucket.editorMode = null
      bucket.detailLoaded = false
    }
  }
})

if(import.meta.webpackHot) {
  import.meta.webpackHot.accept(
    acceptHMRUpdate(useEntityStore, import.meta.hot))
}